/* =========================================================
   PAS · Cart dispatch estimate  (snippets/pas-cart-eta.liquid)

   A custom element, not an inline <script>, for the same reason as the cart
   reward: every cart change replaces #minicart-form through innerHTML, and a
   browser never runs the <script> tags that arrive that way. A custom element
   upgrades itself whenever its markup lands in the DOM.

   The server already printed the right state, so this file only does six
   things: open / close, add the second-shipment fee, take it back out when the
   buyer asks, tick the free split, refund the fee by itself once the cart earns
   the free split, and drop a free split the cart has outgrown. Every mutation
   is followed by a re-render, which is what recalculates the dates and wording.

   The two "by itself" ones live in reconcile(): they read /cart.js and compare
   it with the threshold, so they cannot get out of step with the cart the way a
   flag baked into the markup can.

   Two rules learned the hard way:

   1. /cart/add.js takes a VARIANT id. The product id is a different number
      and Shopify answers 422 "Cannot find variant" — which used to look like
      the button doing nothing at all.

   2. Never reload the page to hide a failure. If the add is refused (product
      unpublished, or missing from this market's catalog) the buyer needs to
      see why, on the panel, in words.
   ========================================================= */
(function () {
  'use strict';

  var ATTR = 'Split shipment';
  var REFUND_NOTE_KEY = 'pas_eta_refund_note_at';
  var REFUND_NOTE_MS = 60000;

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function sessionSet(key, value) {
    try {
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function cartJson() {
    return fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  /* Shopify answers a refused cart call with JSON {status, message,
     description}; fall back to the raw body, then to the status code. */
  function readError(res) {
    return res.text().then(function (text) {
      var out = text;
      try {
        var data = JSON.parse(text);
        out = data.description || data.message || text;
      } catch (e) {}
      return out || 'HTTP ' + res.status;
    });
  }

  function postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) {
        return readError(res).then(function (msg) {
          throw new Error(msg);
        });
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  /* Keep the header counter and the free shipping bar in step — the bar is
     rendered outside #minicart-form, so re-rendering the drawer does not
     touch it. */
  function syncCounters(cart) {
    if (!cart || cart.item_count === undefined) return;
    try {
      var counters = document.querySelectorAll('.cart-count');
      for (var i = 0; i < counters.length; i++) {
        var el = counters[i];
        el.innerHTML = el.classList.contains('cart-count-drawer')
          ? '(' + cart.item_count + ')'
          : cart.item_count > 100
            ? '~'
            : cart.item_count;
      }
      var headerPrice = document.querySelector('header-total-price');
      if (headerPrice && typeof headerPrice.updateTotal === 'function') {
        headerPrice.updateTotal(cart);
      }
      if (window.FreeShipBar && typeof window.FreeShipBar.sync === 'function') {
        window.FreeShipBar.sync();
      }
    } catch (e) {}
  }

  /* The free split is only free while the cart stays over 2x the free-shipping
     threshold, and the paid fee is only owed while it stays under. Both
     directions are handled by PasCartEta#reconcile, which decides from the
     live cart rather than from a flag baked into the markup — the cart keeps
     moving after the markup was made. */

  class PasCartEta extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pasReady === '1') return;
      this.dataset.pasReady = '1';

      this.place = this.getAttribute('data-place') || 'page';
      this.openKey = 'pas_eta_open_' + this.place;
      this.bar = this.querySelector('.pas-eta__bar');

      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);

      /* Make the cart agree with the threshold before anything else: a fee
         that should already be refunded, or a free split the cart has
         outgrown. Then keep doing it while the cart markup changes. */
      this.reconcile();
      this.watch();

      /* A refund that just happened has to be said out loud — the collapsed
         row only shows a different price. It covers free_offer (the fee left
         and nothing was ticked yet) and free_done (the fee left and the split
         was upgraded on the buyer's behalf). */
      var state = this.getAttribute('data-state');
      if (state === 'free_offer' || state === 'free_done') {
        if (this.refundNoteFromMemory()) this.revealUpgrade();
      }

      /* Coming back from a cart change: the element is brand new, so the
         stored flag is the only thing that remembers it was open. */
      if (this.getAttribute('data-open') === '1' || sessionGet(this.openKey) === '1') {
        this.setOpen(true, true);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.watchTimer) {
        clearTimeout(this.watchTimer);
        this.watchTimer = null;
      }
    }

    setOpen(open, silent) {
      this.classList.toggle('is-open', !!open);
      if (this.bar && this.bar.tagName === 'BUTTON') {
        this.bar.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      if (!silent) sessionSet(this.openKey, open ? '1' : null);
    }

    handleClick(event) {
      var target = event.target;

      if (this.bar && this.bar.tagName === 'BUTTON' && this.bar.contains(target)) {
        this.setOpen(!this.classList.contains('is-open'));
        return;
      }

      if (target.closest('[data-eta-add]')) {
        event.preventDefault();
        this.addFee();
        return;
      }

      if (target.closest('[data-eta-remove-fee]') || target.closest('[data-eta-undo]')) {
        event.preventDefault();
        this.undo();
      }
    }

    onChangeFree(event) {
      if (!event.target || !event.target.matches('[data-eta-free]')) return;
      var on = event.target.checked;
      this.run(this.setAttribute_(on ? 'Free' : 'No'));
    }

    addFee() {
      var id = parseInt(this.getAttribute('data-fee-variant'), 10);
      var money = this.getAttribute('data-fee-money') || '';

      if (!id) {
        this.showError(
          'No second-shipment product is selected in Theme settings, so this button cannot work yet.'
        );
        return;
      }

      this.run(
        postJson('/cart/add.js', { items: [{ id: id, quantity: 1 }] })
          .then(function () {
            /* /cart/add.js does not patch cart attributes the way the update
               endpoint does, and the endpoint takes the whole set — so read
               the live cart, then write the note. */
            return this.setAttribute_(money ? 'Paid ' + money : 'Paid');
          }.bind(this))
          .then(function () {
            /* Trust but verify: if the line is still not there, the buyer
               must not be told it worked. */
            return cartJson().then(function (cart) {
              var items = (cart && cart.items) || [];
              for (var i = 0; i < items.length; i++) {
                if (items[i].id === id) return cart;
              }
              throw new Error('The second-shipment fee could not be added to the cart.');
            });
          })
      );
    }

    undo() {
      var key = this.getAttribute('data-fee-key') || '';

      var work = key
        ? postJson('/cart/change.js', { id: key, quantity: 0 }).then(
            function () {
              return this.setAttribute_('No');
            }.bind(this)
          )
        : this.setAttribute_('No');

      this.run(work);
    }

    /* One pass that brings the CART in line with the threshold, run every time
       this block lands on the page and every time the cart markup around it
       changes. Two corrections, and only these two:

         A. a second-shipment fee is in the cart but the cart now qualifies for
            the free split → give the money back (the split itself stays, and
            the buyer lands on the free tick box);
         B. the cart note says the split is Free but the cart has fallen back
            under 2x → wipe the note, so the order cannot claim a free split
            nobody earned, and the buyer sees the paid offer again.

       Why it re-reads /cart.js instead of trusting data-fee-refund and
       data-free-stale: those flags describe the cart at render time, and the
       cart keeps moving. The theme does not always rebuild this block after a
       quantity change (the cart page re-renders the item table on its own),
       so a flag that arrived on a stale render either never fires or fires on
       a cart that has moved on. Deciding from the live cart makes the outcome
       the same no matter which of those happened. */
    reconcile() {
      var el = this;
      var thr2 = parseInt(this.getAttribute('data-thr2'), 10) || 0;
      var feeVariant = parseInt(this.getAttribute('data-fee-variant'), 10) || 0;
      var feeProduct = parseInt(this.getAttribute('data-fee-product'), 10) || 0;
      if (thr2 <= 0 && !feeVariant && !feeProduct) return Promise.resolve();
      if (window.__pasEtaReconciling) return Promise.resolve();
      window.__pasEtaReconciling = true;

      return cartJson()
        .then(function (cart) {
          if (!cart) return 'skip';

          var items = cart.items || [];
          var feeLine = null;
          var base = cart.items_subtotal_price || 0;
          for (var i = 0; i < items.length; i++) {
            var v = parseInt(items[i].variant_id, 10);
            var p = parseInt(items[i].product_id, 10);
            if ((feeVariant && v === feeVariant) || (feeProduct && p === feeProduct)) {
              feeLine = items[i];
              /* The fee is not spend: the threshold has to be measured without
                 it, or paying for the split would buy the free split. */
              base -= items[i].final_line_price || 0;
              break;
            }
          }

          var qualified = thr2 > 0 && base >= thr2;
          var attr = String((cart.attributes && cart.attributes[ATTR]) || '').toLowerCase();
          var freeChosen = attr.indexOf('free') !== -1;

          /* A · paid, now free → hand the money back and KEEP the split.
             The buyer already chose two batches; only the price changed, so
             the note goes straight to "Free" and the order stays split without
             asking them again. Writing "No" here would quietly fold the order
             back into one shipment — the opposite of what they asked for.
             Exception: orphan, where the fee is in the cart but nothing needs
             making, so there is no split left to keep. */
          if (feeLine && qualified) {
            var nowA = Date.now();
            if (window.__pasEtaRefundAt && nowA - window.__pasEtaRefundAt < 8000) return 'skip';
            window.__pasEtaRefundAt = nowA;

            var keepSplit = el.getAttribute('data-state') !== 'orphan';

            return postJson('/cart/change.js', { id: feeLine.key, quantity: 0 })
              .then(function () {
                return el.setAttribute_(keepSplit ? 'Free' : 'No');
              })
              .then(function () {
                /* Only after the line is really gone: the note explains money
                   that left the cart, it must never explain one that did not. */
                sessionSet(REFUND_NOTE_KEY, String(Date.now()));
                if (keepSplit) el.keepOpen();
                return 'refund';
              });
          }

          /* B · free ticked, no longer earned → drop back to the paid offer */
          if (freeChosen && !qualified) {
            var nowB = Date.now();
            if (window.__pasEtaStaleAt && nowB - window.__pasEtaStaleAt < 8000) return 'skip';
            window.__pasEtaStaleAt = nowB;

            return el.setAttribute_('No').then(function () {
              return 'stale';
            });
          }

          /* C · the fee line is gone but the note still says Paid. Happens when
             the shopper deletes the row from the item list instead of pressing
             Remove, or when an earlier attempt died between the two calls. The
             order would arrive saying someone paid for a split that is not
             there, so tidy the note while we are here. */
          if (!feeLine && attr.indexOf('paid') !== -1) {
            var nowC = Date.now();
            if (window.__pasEtaPaidAt && nowC - window.__pasEtaPaidAt < 8000) return 'skip';
            window.__pasEtaPaidAt = nowC;

            return el.setAttribute_('No').then(function () {
              return 'stale';
            });
          }

          return 'ok';
        })
        .then(function (result) {
          window.__pasEtaReconciling = false;
          if (result === 'refund') {
            el.busy(true);
            el.refresh();
          } else if (result === 'stale') {
            /* Re-render so the row stops advertising a free split the cart no
               longer earns. Without this the page keeps the old markup and the
               buyer sees "Two shipments · Free" on a cart that lost the right. */
            el.refresh();
          }
        })
        .catch(function (err) {
          window.__pasEtaReconciling = false;
          el.showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
        });
    }

    /* The theme re-renders the item table on its own for a quantity change, and
       it does not always rebuild this block with it. Watch the container the
       items live in, not this element, and re-check the cart when it moves. */
    watch() {
      var root =
        this.place === 'drawer' ? document.getElementById('minicart-form') : document.querySelector('.main-cart');
      if (!root) root = this.parentElement;
      if (!root || typeof MutationObserver !== 'function') return;

      var el = this;
      this.observer = new MutationObserver(function () {
        if (el.watchTimer) clearTimeout(el.watchTimer);
        el.watchTimer = setTimeout(function () {
          el.watchTimer = null;
          el.reconcile();
        }, 350);
      });
      this.observer.observe(root, { childList: true, subtree: true });
    }

    /* The refund reloads the cart, and the server cannot know a fee was just
       handed back — so the sentence explaining the missing money is replayed
       from sessionStorage for a minute after the fact. Both the drawer and the
       cart page render their own panel, so both may add it; each is only ever
       seen by the buyer in the one that is on screen.
       Cleared once shown: it explains a single event, not a lasting state. */
    refundNoteFromMemory() {
      var at = parseInt(sessionGet(REFUND_NOTE_KEY) || '0', 10);
      if (!at || Date.now() - at > REFUND_NOTE_MS) return false;
      this.showRefundNote();
      sessionSet(REFUND_NOTE_KEY, null);
      return true;
    }

    /* The panel is display:none while the row is collapsed, so the sentence
       would never be seen. Open it for this one load and put the block in the
       middle of the screen — a refund the buyer never notices looks like the
       money simply vanished. The stored open flag is wiped afterwards so this
       does not turn into a permanent preference. */
    revealUpgrade() {
      this.setOpen(true, true);
      sessionSet(this.openKey, null);
      if (this.place !== 'page') return;

      var el = this;
      setTimeout(function () {
        try {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (e) {
          try {
            el.scrollIntoView();
          } catch (e2) {}
        }
      }, 300);
    }

    showRefundNote() {
      var panel = this.querySelector('.pas-eta__panel');
      if (!panel || panel.querySelector('.pas-eta__note--refund')) return;

      var money = this.getAttribute('data-fee-money') || '';
      var paid = money ? 'the ' + money + ' you paid' : 'the fee you paid';
      var p = document.createElement('p');
      p.className = 'pas-eta__note pas-eta__note--refund';
      p.textContent =
        'Your cart now earns the free second shipment, so ' +
        paid +
        ' has been taken back out.' +
        (this.getAttribute('data-state') === 'free_done'
          ? ' Your two batches stay split, exactly as you chose.'
          : '');

      var sub = panel.querySelector('.pas-eta__sub');
      if (sub && sub.parentNode) sub.parentNode.insertBefore(p, sub.nextSibling);
      else panel.appendChild(p);
    }

    /* Reads the live cart first so attributes another app added are kept —
       the endpoint takes the whole attribute set, not a patch. */
    mergedAttributes(value, cart) {
      var out = {};
      if (cart && cart.attributes) {
        for (var k in cart.attributes) {
          if (Object.prototype.hasOwnProperty.call(cart.attributes, k)) out[k] = cart.attributes[k];
        }
      }
      out[ATTR] = value;
      return out;
    }

    setAttribute_(value) {
      return cartJson().then(function (cart) {
        return postJson('/cart/update.js', { attributes: this.mergedAttributes(value, cart) });
      }.bind(this));
    }

    keepOpen() {
      sessionSet(this.openKey, '1');
    }

    busy(on) {
      this.classList.toggle('is-busy', !!on);
      this.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    /* One place for busy → refresh, and for busy → explain. */
    run(work) {
      this.busy(true);
      this.clearError();
      return work
        .then(
          function () {
            this.keepOpen();
            this.refresh();
          }.bind(this)
        )
        .catch(
          function (err) {
            this.busy(false);
            this.showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
          }.bind(this)
        );
    }

    showError(message) {
      try {
        console.error('[pas-cart-eta]', message);
      } catch (e) {}

      var panel = this.querySelector('.pas-eta__panel');
      if (!panel) return;

      /* The panel is display:none while the row is collapsed — open it so the
         message is actually visible. */
      this.setOpen(true);

      var el = panel.querySelector('.pas-eta__err');
      if (!el) {
        el = document.createElement('p');
        el.className = 'pas-eta__err';
        panel.insertBefore(el, panel.firstChild.nextSibling);
      }
      el.textContent = message;
    }

    clearError() {
      var el = this.querySelector('.pas-eta__err');
      if (el) el.parentNode.removeChild(el);
    }

    refresh() {
      var form = document.getElementById('minicart-form');

      if (this.place === 'drawer' && form) {
        fetch('/cart?section_id=minicart-form', { cache: 'no-store', credentials: 'same-origin' })
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            var next = new DOMParser().parseFromString(html, 'text/html').querySelector('#minicart-form');
            if (!next) {
              window.location.reload();
              return;
            }
            form.innerHTML = next.innerHTML;
            return cartJson().then(syncCounters);
          })
          .catch(function () {
            window.location.reload();
          });
        return;
      }

      window.location.reload();
    }
  }

  if (!customElements.get('pas-cart-eta')) {
    customElements.define('pas-cart-eta', PasCartEta);
  }

  /* the tick box lives inside the element but fires change, not click */
  document.addEventListener('change', function (event) {
    var host = event.target && event.target.closest ? event.target.closest('pas-cart-eta') : null;
    if (host && typeof host.onChangeFree === 'function') host.onChangeFree(event);
  });
})();
